/**
 * Password Step Component
 * TASK-339: Onboarding Wizard UI
 *
 * Step 1: Set password for admin account
 */

import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminOnboardingService } from '../../../services/adminOnboardingService';

interface PasswordStepProps {
  token: string;
  firstName: string;
  lastName: string;
  onComplete: () => void;
}

const PasswordStep: React.FC<PasswordStepProps> = ({
  token,
  firstName: initialFirstName,
  lastName: initialLastName,
  onComplete,
}) => {
  // Form state
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);

  // Password validation
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  // Validate password as user types
  useEffect(() => {
    setPasswordValidation({
      minLength: password.length >= 12,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\x5b\x5d{}|;':",.\x2f<>?]/.test(password),
    });
  }, [password]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const response = await adminOnboardingService.setPassword({
        token,
        password,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
      });

      if (response.success) {
        toast.success('Password set successfully!');
        onComplete();
      } else {
        toast.error('Failed to set password');
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to set password';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Name fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
            placeholder="John"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
            placeholder="Doe"
          />
        </div>
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
            placeholder="Create a strong password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>

      {/* Password requirements */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <p className="text-xs font-medium text-gray-500 mb-2">Password requirements:</p>
        <PasswordRequirement met={passwordValidation.minLength} text="At least 12 characters" />
        <PasswordRequirement met={passwordValidation.hasUppercase} text="One uppercase letter" />
        <PasswordRequirement met={passwordValidation.hasLowercase} text="One lowercase letter" />
        <PasswordRequirement met={passwordValidation.hasNumber} text="One number" />
        <PasswordRequirement met={passwordValidation.hasSpecial} text="One special character (!@#$%^&*)" />
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={`
              w-full px-3 py-2 pr-10 border rounded-lg text-sm
              focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent
              ${confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'}
            `}
            placeholder="Confirm your password"
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {confirmPassword && !passwordsMatch && (
          <p className="mt-1 text-xs text-red-600">Passwords do not match</p>
        )}
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={isLoading || !isPasswordValid || !passwordsMatch}
        className="w-full py-3 px-4 bg-kielis-navy text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Setting Password...
          </>
        ) : (
          'Continue'
        )}
      </button>
    </form>
  );
};

// Helper component for password requirements
const PasswordRequirement: React.FC<{ met: boolean; text: string }> = ({ met, text }) => (
  <div className="flex items-center gap-2">
    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${met ? 'bg-green-100' : 'bg-gray-200'}`}>
      {met && <CheckCircle size={12} className="text-green-600" />}
    </div>
    <span className={`text-xs ${met ? 'text-green-700' : 'text-gray-500'}`}>{text}</span>
  </div>
);

export default PasswordStep;
